import { PropertyInfoCollector } from "./PropertyInfoCollector";
import { PropertyData, ReadyData } from "./PropertyInfoCollectorTypes";
import "../css/propertyInfoStyle.css";
import {
  GlassFoldUdBox,
  GroupSelector,
  KeyValueLine,
  KeyValuesBoxGrid,
  KeyValuesHorizontal,
  PropertyMapContainer,
  ShowSelectedGroup,
  ViewButtonDropdown,
} from "./PropertyComponents";
import { PropInfoGroupFormatters } from "./PropertyInfoGroupFormatters";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { Cookies } from "@viamap/viamap2-common";
import { MitStorage } from "src/managers/MitStorage";
import { FavEditor } from "./FavEditor";
import { GlassButton } from "src/components/GlassButtons";
import { Fragment, useContext, useEffect, useState } from "react";
import { message } from "src/components/Message";
import { EnergyLabelSummary } from "src/components/EnergyLabelComponents";
import { EjendomAnnotationsTable } from "src/modules/ANNModule/ANNComponents";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { useMitPersistenceState } from "src/managers/MitPersistenceState";
import { PropertyInfoTextOnMap } from "src/components/PropertyInfoTextOnMap";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { MapitStateContext } from "src/states/MapitState";
import { featureCollection } from "@turf/helpers";
import { MarkData } from "src/components/MarkData";
import { useTingbogSectionElements } from "./TingbogPropertySections";
import { ServitutRedegoerelseLaunchButton } from "src/components/ServitutRedegoerelseRapport";

type Props = {
  callBackOnLoaded?: (data: PropertyData) => void;
  bfeNr: string;
  ejerlavskode?: string;
  matrnr?: string;
  allowExtendedInformation: boolean;
  foldetUd?: boolean;
  cols?: number;
};

export function PropertyInfoView(props: Props) {
  const { hasAccessToFeature } = useContext(ApplicationStateContext);
  const { dispatch: mapitDispatch } = useContext(MapitStateContext);
  const [collector] = useState(
    new PropertyInfoCollector({
      BFEnr: props.bfeNr,
    })
  );

  function setSelectedGroup(string) {
    Cookies.setCookie("PropertyInfoSelectedTab", string)
    _setSelectedGroup(string)
  }

  const [selectedGroup, _setSelectedGroup] = useState(
    Cookies.getCookie("PropertyInfoSelectedTab") || (hasAccessToFeature(Feature.PropertyInfoFavorite) && "Favoritter") || "Ejendom"
  );

  const [availableFavorites, setAvailableFavorites] = useMitPersistenceState("PropertyFavorites", { "minFavorit": [] });

  const [selectFav] = useState<string>(
    MitStorage.getValue("PropertySelectedFav") as string || "minFavorit"
  )

  const [showFavEditor, setShowFavEditor] = useState(false);



  const [availableGroup, setAvailableGroup] = useState(hasAccessToFeature(Feature.PropertyInfoFavorite) ? ["Favoritter"] : []);
  const [data, setData] = useState<Partial<PropertyData>>({});
  const [loading, setLoading] = useState(true)
  let [select, setSelect] = useState("Kontor");
  const tingbogSections = useTingbogSectionElements({
    data,
    hasAccessToFeature,
    foldetUd: props.foldetUd || false,
    cadastralHints: {
      ejerlavskode: props.ejerlavskode,
      matrikelnummer: props.matrnr,
    },
    onCopyServitutterToExcel: (event, displayData) => {
      servitutTable(displayData, event.ctrlKey, event.altKey);
    },
    extraServitutActions: (displayData) => (
      <ServitutRedegoerelseLaunchButton
        propertyData={displayData}
        hasAccessToFeature={hasAccessToFeature}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      />
    ),
  });


  useEffect(() => {
    getDataAsync();
  }, [hasAccessToFeature]);

  function getDataAsync() {
    function setOnCallback<T extends keyof PropertyData>(
      val: ReadyData<PropertyData[T]>,
      key: T
    ) {
      if (val.status == "Ready" && (val.data || key === "ViamapTingBogAll")) {
        setData((a) => {
          return { ...a, [key]: val.data ?? (key === "ViamapTingBogAll" ? [] : val.data) };
        });
      }
      if (val.status == "Error" && key === "ViamapTingBogAll") {
        setData((a) => {
          return { ...a, ViamapTingBogAll: [] };
        });
      }
    }
    if (props.allowExtendedInformation) {
      collector.requestData("ViamapTingBog", setOnCallback);
      collector.requestData("ViamapTingBogAll", setOnCallback);
    }
    if (hasAccessToFeature(Feature.Markoversigt)) {
      collector.requestData("Marker", setOnCallback);
    }
    collector.requestData("ViamapEjendomData", setOnCallback);
    collector.requestData("DHM_Overflade", setOnCallback);
    collector.requestData("DHM_Terraen", setOnCallback);
    collector.requestData("ViamapHandelsOplysning", setOnCallback);
    collector.requestData("GQL_EBRBFE", setOnCallback);
    collector.requestData("Vurd", setOnCallback);
    collector.requestData("PlanDataDelområde", setOnCallback);
    collector.requestData("PlanDataLokalPlan", setOnCallback);
    collector.requestData("PlanDataKommunePlan", setOnCallback);
    collector.requestData("ViamapEjendomStats", setOnCallback);
    collector.requestData("EnergyLabel", setOnCallback);
    collector.requestData("GQL_BBR", setOnCallback);
    collector.requestData("GQL_BFE", setOnCallback);
    collector.requestData("GQL_VURD", setOnCallback);


    if (hasAccessToFeature(Feature.PlandataAflyst)) {
      collector.requestData("PlanDataKommunePlanAflyst", setOnCallback)
      collector.requestData("PlanDataLokalPlanAflyst", setOnCallback)
    }

    if (hasAccessToFeature(Feature.PlandataForslag)) {
      collector.requestData("PlanDataKommunePlanForslag", setOnCallback)
      collector.requestData("PlanDataLokalPlanForslag", setOnCallback)
    }
    collector.callBackOnFinished((data: PropertyData) => {
      props.callBackOnLoaded?.(data);
      setLoading(false)
    });
  }

  if (Object.keys(data).length === 0) {
    return <>Loading...</>;
  }

  function PrintHeader() {
    let printDate = new Date().toLocaleDateString();

    return (
      <div className="only-print print-header" style={{}}>
        <div style={{ display: "inline-block" }}>
          <h4>Ejendomsinformation</h4>
          <h5>Adresse: {data?.ViamapEjendomData?.ejdAdresse}</h5>
          <h5>Printed: {printDate}</h5>
        </div>
        {/* <div style={{float:"right", marginRight:"25px"}}> <div className={"mit-viamap-icon"} id={"mit-viamap-icon"}/></div> */}
      </div>
    );
  }

  function harFælleslodder(BBRSFE: any): boolean {
    return (BBRSFE?.[0]?.properties?.jordstykke?.filter((elm: any) => elm?.properties?.samletFastEjendomBFEnummer !== BBRSFE?.[0]?.properties["BFEnummer"])?.length || false);
  }

  const annotationBfeNumber =
    data.BBRSFE?.[0]?.properties?.BFEnummer
    ?? data.ViamapEjendomData?.sag?.BFEnr
    ?? data.GQL_BBR?.MAT_SamletFastEjendom?.nodes?.[0]?.BFEnummer
    ?? props.bfeNr;

  return (
    <>
      {/* {loading} */}
      <PrintHeader />

      {/* <NavigationBarSimple title='Ejendoms Info' > */}
      {/* <div className='Paper-MitProp'> */}
      {/* <div className='Container-MitProp' style={{padding:"0.5cm"}}> */}
      {/* <ErrorMessage errors={collector.errors} /> */}
      {/* ----------- AREAL ------------ */}
      {SettingsManager.getSystemSetting("debuggingShowExtraDebugging") ?
        <GlassButton onClick={() => console.info(data)} >Log to console</GlassButton>
        : null}

      <KeyValuesHorizontal>
        {PropInfoGroupFormatters.Ejendommen(data, hasAccessToFeature(Feature.DeviceScreenSizePhone)).map((a) => {
          return (
            <Fragment key={a.key}>
              <KeyValueLine
                key={a.key}
                valKey={a.key}
                value={a.val}
                keepEmpty={true}
              />
            </Fragment>
          );
        })}
      </KeyValuesHorizontal>
      <GroupSelector
        selected={selectedGroup}
        group={availableGroup}
        onSelect={(group) => {
          ActionTracker.track("PropertyInfo", "Tab", "Select", { tabName: group });
          setSelectedGroup(group)
        }}
      />
      {selectedGroup == "Favoritter" ?
        <>
          <div style={{ marginLeft: "auto", marginRight: "10px", marginTop: "10px" }}>
            <GlassButton onClick={() => setShowFavEditor(true)} style={{
              padding: "2px 6px",
              fontSize: "11px",
              minWidth: "auto",
              width: "auto",
              height: "auto",
              minHeight: "auto",
              aspectRatio: "unset",
              display: "flex",
              alignItems: "center",
              gap: "3px",
              lineHeight: "1"
            }}>
              <MIBIcon icon="list" />
              {Localization.getText("Edit")}
            </GlassButton>
          </div>
          <FavEditor show={showFavEditor} favBoxes={availableFavorites} setFavBoxes={setAvailableFavorites} onClose={() => setShowFavEditor(false)} />
        </>
        : null}
      <div className="Scroll-content">
        <ShowSelectedGroup
          keepSelected={true}
          loading={loading}
          columns={props.cols!}
          favBoxes={availableFavorites}
          favName={selectFav}
          group={selectedGroup}
          groupCallBack={(groups) =>
            setAvailableGroup([...new Set([...(hasAccessToFeature(Feature.PropertyInfoFavorite) ? ["Favoritter"] : []), "Ejendom", ...groups, ...(hasAccessToFeature(Feature.AnnotationModule) ? [Localization.getText("Annotations")] : []), "Alle"])])
          }
        >
          {data?.["EBRAdresseOgPunkt"] ? (
            <GlassFoldUdBox
              key={"map"}
              title="Område"
              boxKey="Område"
              group="Overblik"
              foldetUd={props.foldetUd || false}
            >
              {data?.["EBRAdresseOgPunkt"] &&
                data.EBRAdresseOgPunkt?.[0]?.lat && data.EBRAdresseOgPunkt?.[0]?.lng && (
                  <PropertyMapContainer location="Område" >
                    <PropertyInfoTextOnMap
                      orthoPhoto={false}
                      lat={data.EBRAdresseOgPunkt[0].lat}
                      lng={data.EBRAdresseOgPunkt[0].lng}
                    />
                  </PropertyMapContainer>
                )}
            </GlassFoldUdBox>
          ) : (
            <></>
          )}
          {data?.["EBRAdresseOgPunkt"] ? (
            <GlassFoldUdBox
              key={"mapOrtofoto"}
              title="Ortofoto"
              boxKey="Ortofoto"
              group="Overblik"
              foldetUd={props.foldetUd || false}
            >
              {data?.["EBRAdresseOgPunkt"] &&
                data.EBRAdresseOgPunkt?.[0]?.lat && data.EBRAdresseOgPunkt?.[0]?.lng &&
                (
                  <PropertyMapContainer location="Ortofoto" >
                    <PropertyInfoTextOnMap
                      orthoPhoto={true}
                      lat={data.EBRAdresseOgPunkt[0].lat}
                      lng={data.EBRAdresseOgPunkt[0].lng}
                    />
                  </PropertyMapContainer>
                )}
            </GlassFoldUdBox>
          ) : (
            <></>
          )}
          {data.ViamapEjendomData ? (
            <GlassFoldUdBox
              title="Arealer"
              boxKey="Arealer"
              group="Ejendom"
              foldetUd={props.foldetUd || false}
            >
              <KeyValuesBoxGrid minWidth="200px" maxCols={3}>
                {PropInfoGroupFormatters.ArealerGroup(data).map((a) => {
                  return (
                    <Fragment key={a.key}>
                      <KeyValueLine
                        key={a.key}
                        valKey={a.key}
                        value={a.val}
                        infoText={a.infoText}
                        keepEmpty={true}
                      />
                    </Fragment>
                  );
                })}
              </KeyValuesBoxGrid>
            </GlassFoldUdBox>
          ) : (
            <></>
          )}
          {data.DHM_Terraen && data.DHM_Overflade ? (
            <GlassFoldUdBox
              title="Højder"
              boxKey="Højder"
              group="Ejendom"
              foldetUd={props.foldetUd || false}
            >
              <KeyValuesBoxGrid minWidth="150px">
                <KeyValueLine valKey="Højde, grund" value={data.DHM_Terraen} />
                <KeyValueLine
                  valKey="Højde, hus (omtrentligt)"
                  value={
                    data.DHM_Overflade &&
                    data.DHM_Terraen &&
                    data.DHM_Overflade - data.DHM_Terraen
                  }
                />
              </KeyValuesBoxGrid>
            </GlassFoldUdBox>
          ) : (
            <></>
          )}
          {data.ViamapHandelsOplysning ? (
            <GlassFoldUdBox
              title="Handelsoplysninger"
              group="Ejendom"
              boxKey="Handelsoplysninger"
              foldetUd={props.foldetUd || false}
            >
              {PropInfoGroupFormatters.HandelsOplysninger(data)}
            </GlassFoldUdBox>
          ) : (
            <></>
          )}
          {data.ViamapEjendomData ? (
            <GlassFoldUdBox
              title="Ejerforhold"
              group="Ejendom"
              boxKey="Ejerforhold"
              foldetUd={props.foldetUd || false}
            >
              {PropInfoGroupFormatters.EjerforholdData(data).map((a, idx) => {
                return <Fragment key={idx}>{a}</Fragment>;
              })}
            </GlassFoldUdBox>
          ) : (
            <></>
          )}
          {data.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0] ? (
            <GlassFoldUdBox
              title="Samlet Fast Ejendom"
              group="Ejendom"
              boxKey="Samlet Fast Ejendom"
              foldetUd={props.foldetUd || false}
            >
              {PropInfoGroupFormatters.SFEGQL(data.GQL_BFE, data)}
            </GlassFoldUdBox>
          ) : (
            <></>
          )}
          <GlassFoldUdBox
            title="Dokumenter"
            group="Dokumenter"
            boxKey="Dokumenter"
            foldetUd={props.foldetUd || false}
          >
            {PropInfoGroupFormatters.DocumentsComponent(data)}
          </GlassFoldUdBox>

          {data.EnergyLabel ? (
            <GlassFoldUdBox
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {Localization.getText("Energy Marking")}
                  <EnergyLabelSummary data={data} />
                </div>
              }
              group="Ejendom"
              boxKey="Energimærkning"
              foldetUd={props.foldetUd || false}
            >
              {PropInfoGroupFormatters.EnergyInfo(data)}
            </GlassFoldUdBox>
          ) : (
            <></>
          )}

          {/* <GlassFoldUdBox
            title="POI"
            group="POI"
            foldetUd={props.foldetUd || false}
          >
            {("EBRAdresseOgPunkt" in data &&
              data.EBRAdresseOgPunkt?.[0]?.lat && data.EBRAdresseOgPunkt?.[0]?.lng &&
              (
                <App
                  className="no-print"
                  group="POI"
                  lngLat={{ lat: data.EBRAdresseOgPunkt[0].lat, lng: data.EBRAdresseOgPunkt[0].lng }}
                />
              )) || <></>}
          </GlassFoldUdBox> */}

          {"MatUdsnit" in data && data.MatUdsnit ? (
            <GlassFoldUdBox
              title="Mat Oversigt"
              group="Matrikler"
              boxKey="Mat Oversigt"
              foldetUd={false}
            >
              {PropInfoGroupFormatters.MatrikelKort(data.MatUdsnit)}
            </GlassFoldUdBox>
          ) : <></>}
          {data.Marker?.marker?.length ?
            <GlassFoldUdBox
              title={<>Marker - kort<span style={{ marginLeft: "auto" }} ><ViewButtonDropdown title="Add to map"
                options={[
                  { value: "markAfgrænset", text: Localization.getText("Add fields cut to property") },
                  { value: "markAlle", text: Localization.getText("Add fields overlapping property") }
                ]}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onSelect={(key) => {
                  if (key == "markAfgrænset") {
                    let fc2 = featureCollection((data?.Marker?.marker ?? []).map(MarkData.areaFormater).map((a) => ({ ...a, geometry: a.intersect })) as any ?? [])
                    MarkData.markLayerInfo(Localization.getText("Fields cut to property"), ["Marknr", "overlap"], fc2, mapitDispatch);
                  } else if (key == "markAlle") {
                    let fc2 = featureCollection((data?.Marker?.marker ?? []).map(MarkData.areaFormater) as any ?? [])
                    MarkData.markLayerInfo(Localization.getText("Fields overlapping property"), ["Marknr", "area"], fc2, mapitDispatch);
                  }
                }}
              ><MIBIcon icon="addToMap" /></ViewButtonDropdown></span></>}
              group="Marker"
              boxKey="Markerkort"
              foldetUd={props.foldetUd || false} >
              {PropInfoGroupFormatters.MarkerMap(data)}
            </GlassFoldUdBox>
            : <></>}
          {data.Marker?.marker?.length ?
            <GlassFoldUdBox
              title="Marker - detaljer"
              group="Marker"
              boxKey="Marker"
              foldetUd={props.foldetUd || false}
            >
              {PropInfoGroupFormatters.MarkerTable(data)}
            </GlassFoldUdBox>
            : <></>}
          {data.GQL_BFE ? (
            <GlassFoldUdBox
              title="Matrikler"
              group="Matrikler"
              boxKey="Matrikler"
              foldetUd={props.foldetUd || false}
            >
              {PropInfoGroupFormatters.MatriklerTable(data, hasAccessToFeature, props.matrnr, props.ejerlavskode)}
            </GlassFoldUdBox>
          ) : (
            <></>
          )}
          {data.GQL_BFE && harFælleslodder(data.GQL_BFE) ? (
            <GlassFoldUdBox
              title="Fælleslodder"
              group="Matrikler"
              boxKey="Fælleslodder"
              foldetUd={props.foldetUd || false}
            >
              {PropInfoGroupFormatters.FaelleslodderTable(data, hasAccessToFeature)}
            </GlassFoldUdBox>
          ) : (
            <></>
          )}
          {"BBRUdsnit" in data && data.BBRUdsnit ? (
            <GlassFoldUdBox
              title="BBR Oversigt"
              group="BBR"
              boxKey="BBR Oversigt"
              foldetUd={false}
            >
              {PropInfoGroupFormatters.BBRKort(data.BBRUdsnit)}
            </GlassFoldUdBox>
          ) : <></>}


          {data.GQL_BBR?.MAT_SamletFastEjendom ? (
            <GlassFoldUdBox
              group="BBR"
              title="BBR"
              boxKey="BBR"
              foldetUd={props.foldetUd || false}
            >
              {PropInfoGroupFormatters.BBRQGL(data.GQL_BBR)}
            </GlassFoldUdBox>
          ) : (
            <></>
          )}
          {data.GQL_BBR?.MAT_SamletFastEjendom ? (
            <GlassFoldUdBox
              group="BBR"
              title="BBR Teknik"
              boxKey="BBR Teknik"
              foldetUd={props.foldetUd || false}
            >
              {PropInfoGroupFormatters.BBRQGLTeknikGrund(data.GQL_BBR)}
            </GlassFoldUdBox>
          ) : (
            <></>
          )}

          {data.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0]?.jordstykkeSamlesISamletFastEjendom?.nodes ? (
            PropInfoGroupFormatters.GQL_BFE(data.GQL_BFE).map((a) => {
              const j = a.jordstykke;
              const excludedKeys = [
                "centroide",
                "forretningsomraade",
                "forretningsproces",
                "id_lokalId",
                "id_namespace",
                "registreringTil",
                "stammerFraJordstykkeLokalId",
                "supplerendeMaalingSagLokalId",
                "virkningTil",
                "jordstykkeLiggerIEjerlav",
                "jordstykkeLiggerIMatrikelKommune",
                "jordstykkeLiggerIMatrikelRegion",
                "jordstykkeLiggerIMatrikelSogn",
                "centroideLiggerIndenForJordstykke",
              ];
              return (
                <GlassFoldUdBox
                  key={a.key}
                  title={"Matrikel " + a.name}
                  group="Matrikler"
                  boxKey={"Matrikler Seperat"}
                  foldetUd={props.foldetUd || false}
                >
                  {Object.keys(j)
                    .filter((key) => !excludedKeys.includes(key) && j[key] != null)
                    .map((key) => (
                      <KeyValueLine
                        key={key}
                        valKey={key}
                        keepEmpty
                        value={j[key]}
                      />
                    ))}
                  {hasAccessToFeature(Feature.Jordforureningsattest) && j.ejerlavskode && j.matrikelnummer ? (
                    <KeyValueLine
                      key={"Jordforureningsattest"}
                      valKey={"Jordforureningsattest fra Danmarks Miljøportal"}
                      keepEmpty
                      value={
                        <a
                          href={`https://jord.miljoeportal.dk/report?elav=${encodeURIComponent(String(j.ejerlavskode))}&matrnr=${encodeURIComponent(String(j.matrikelnummer))}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Generér
                        </a>
                      }
                    />
                  ) : null}
                </GlassFoldUdBox>
              );
            })
          ) : <></>}

          {tingbogSections}


          {(hasAccessToFeature(Feature.BoligmarkedsStatistik) &&
            data.ViamapEjendomStats && (
              <GlassFoldUdBox
                group="Boligmarkedsstatistik"
                title="Realiceret handelspris, Parcel-/rækkehus"
                foldetUd={props.foldetUd || false}
              >
                {PropInfoGroupFormatters.ViamapMarkedsStatistik(data)}
              </GlassFoldUdBox>
            )) || <></>}

          {data.PlanDataDelområde && PropInfoGroupFormatters.Delområde(data, props.foldetUd || false)}
          {data.PlanDataLokalPlan && PropInfoGroupFormatters.LokalPlan(data, props.foldetUd || false)}
          {data.PlanDataKommunePlan && PropInfoGroupFormatters.KommunePlan(data, props.foldetUd || false)}
          {data.PlanDataLokalPlanAflyst && PropInfoGroupFormatters.PlanDataLokalPlanAflyst(data)}
          {data.PlanDataKommunePlanAflyst && PropInfoGroupFormatters.PlanDataKommuneAflyst(data)}


          {data.PEnheder ? (
            <GlassFoldUdBox
              group="Erhverv"
              title="Virksomheder på Ejendommen"
              boxKey="Virksomheder på Ejendom"
              foldetUd={props.foldetUd || false}
            >
              {PropInfoGroupFormatters.PEnhederPaaEjendomFormatter(data)}
            </GlassFoldUdBox>
          ) : (
            <></>
          )}

          {(hasAccessToFeature(Feature.ExplorerMarkedsData) &&
            PropInfoGroupFormatters.MarkedsDataNewsec(
              data as any,
              props.foldetUd || false,
              hasAccessToFeature,
              select,
              setSelect
            )) || <></>}
          {PropInfoGroupFormatters.Vurderinger(data, props.foldetUd || false)}

          {hasAccessToFeature(Feature.AnnotationModule) ? (
            <EjendomAnnotationsTable
              bfeNumber={annotationBfeNumber}
              group={Localization.getText("Annotations")}
            />
          ) : (
            <></>
          )}
        </ShowSelectedGroup>
      </div >
    </>
  );
}



function servitutTable(data, ctrl = false, alt = false) {

  let Text = data.ViamapTingBog?.servitutter?.toSorted((a, b) => {
    const IA = (d) => Number(d.prioritetNummer) || 10000;
    if (IA(a) < IA(b))
      return -1
    if (IA(a) > IA(b)) {
      return 1
    }
    return 0
  }).map((servitut) => {
    function TingBogRecordNumber(a: string) {
      if (alt)
        return a.replace(/(.{4})(.{2})(.{2})-()/, "$3.$2.$1-$4")
      return a.replace(/(.{4})(.{2})(.{2})-()/, "$3$2$1-$4")
    }

    let recordNo = TingBogRecordNumber(servitut.aktId || servitut.aktAliasId);
    if (ctrl) {
      return `${servitut.prioritetNummer?.toString()}\t"${recordNo}\n${(servitut.tekstSummarisk || servitut.tekster?.join(", ") || "")}"`;
    }
    return `${servitut.prioritetNummer?.toString()}\t${recordNo}\t${(servitut.tekstSummarisk || servitut.tekster?.join(", ") || "")}`;
  }) || ["No data"];

  navigator.clipboard.writeText(Text.join("\n")).then(() => {
    message.info("Copied to clipboard (Excel format)")
  });
}